// LOS CAMPOS DEL MEDICO, COMO SE DIBUJAN EN LA PANTALLA.
//
// Punto 10, fase 1. Lissette, 2026-09-16: *"si por persona, que lo vean todos,
// arranca la fase 1"*.
//
// ── ESTA LISTA ES LA MISMA QUE LA DEL SERVIDOR ──────────────────────────────
//
// `deskte-api/src/utils/medicosDelCliente.ts` lleva `CAMPOS_DEL_MEDICO` y
// `LARGO_MAXIMO` con estos mismos nombres y estos mismos topes, y
// `tests/unit/medicosDelCliente.test.ts` compara los dos ficheros. Un campo que
// este formulario mande y el servidor no recoja se guardaria en el aire sin dar
// ningun error —la trampa de `pickData` del CLAUDE.md— y uno que el servidor
// recorte a menos de lo que este deja escribir se cortaria en silencio.
//
// De donde salen: son los campos que la agencia escribe A MANO en el cuadro de
// texto de Apizeal (PCP, NPI, centro medico, direccion, telefono, fax) mas los
// dos que hacian falta —la red que acepta y de donde salio el dato— y las notas.

import { consultaDeUnaLinea } from './direccionUsa.js';

/** Orden de los campos en el formulario y en la tarjeta. */
export const CAMPOS_DEL_MEDICO = [
  'nombre',
  'especialidad',
  'npi',
  'centro',
  'direccion',
  'ciudad',
  'estado',
  'zip',
  'telefono',
  'fax',
  'red',
  'notas',
  'origen',
];

/** Los mismos topes que el servidor. Se pasan al `maxLength` del campo. */
export const LARGO_MAXIMO = {
  nombre: 160,
  especialidad: 80,
  npi: 20,
  centro: 160,
  direccion: 240,
  ciudad: 80,
  estado: 40,
  zip: 10,
  telefono: 40,
  fax: 40,
  red: 160,
  notas: 2000,
  origen: 300,
};

/**
 * Como se llama cada campo en pantalla, y con que ayuda.
 *
 * `ancho: 2` = ocupa la fila entera. `area: true` = va en un area de texto.
 */
export const ETIQUETAS = {
  nombre: {
    es: 'Médico', en: 'Doctor', ancho: 2,
    pistaEs: 'Dr. Juan Pérez, MD', pistaEn: 'Dr. Jane Doe, MD',
  },
  especialidad: {
    es: 'Especialidad', en: 'Specialty',
    pistaEs: 'PCP, Ginecología, Dental…', pistaEn: 'PCP, OB/GYN, Dental…',
  },
  npi: { es: 'NPI', en: 'NPI', pistaEs: '1003012345', pistaEn: '1003012345' },
  centro: {
    es: 'Centro médico', en: 'Medical center', ancho: 2,
    pistaEs: 'Clínica / hospital', pistaEn: 'Clinic / hospital',
  },
  // Calle, ciudad, estado y ZIP aparte (Lissette 2026-10-25: "en la dirección
  // del Dr. me gustaría dirección aparte, ciudad aparte, estado aparte, zip
  // code"). `direccion` es la calle; se queda con su nombre para que los médicos
  // guardados antes, con todo en un renglón, sigan leyéndose.
  direccion: { es: 'Dirección (calle)', en: 'Street address', ancho: 2, pistaEs: '1450 W 49th St, Suite 210', pistaEn: '1450 W 49th St, Suite 210' },
  ciudad: { es: 'Ciudad', en: 'City', ancho: 2, pistaEs: 'Hialeah', pistaEn: 'Hialeah' },
  estado: { es: 'Estado', en: 'State', pistaEs: 'FL', pistaEn: 'FL' },
  zip: { es: 'ZIP code', en: 'ZIP code', pistaEs: '33012', pistaEn: '33012' },
  telefono: { es: 'Teléfono', en: 'Phone', pistaEs: '(954) 555-0123', pistaEn: '(954) 555-0123' },
  fax: { es: 'Fax', en: 'Fax', pistaEs: '', pistaEn: '' },
  red: {
    es: 'Red / aseguradora que acepta', en: 'Network / carrier accepted', ancho: 2,
    pistaEs: 'Florida Blue HMO, Ambetter…', pistaEn: 'Florida Blue HMO, Ambetter…',
  },
  notas: { es: 'Notas', en: 'Notes', ancho: 2, area: true, pistaEs: '', pistaEn: '' },
  // La costumbre buena de Apizeal: en la captura lo escriben a mano al final
  // ("Información tomada de la orden de referido con fecha 02/10/2026"), y es lo
  // que permite que otra persona confie en el dato seis meses despues.
  origen: {
    es: '¿De dónde salió este dato?', en: 'Where did this come from?', ancho: 2,
    pistaEs: 'Orden de referido del 02/10/2026, lo dijo el cliente por teléfono…',
    pistaEn: 'Referral order dated 02/10/2026, client said so by phone…',
  },
};

/** El formulario vacio: todas las claves puestas a "" (nunca `undefined`). */
export function medicoVacio(persona = 'primary') {
  const o = { persona };
  for (const c of CAMPOS_DEL_MEDICO) o[c] = '';
  return o;
}

/**
 * Un medico que vino del servidor, como formulario.
 *
 * Los guardados antes del 2026-10-25 tienen la direccion entera en un renglón
 * ("1450 W 49th St, Hialeah, FL 33012") y ciudad/estado/ZIP vacios. Al abrirlos
 * se parte el renglón en sus casillas para que la agente lo vea ya separado y lo
 * revise antes de guardar. Si no se reconoce ni ciudad ni ZIP, se deja entero en
 * la calle: mejor un renglón entero que uno partido mal.
 */
export function medicoComoFormulario(m) {
  const o = medicoVacio((m && m.persona) || 'primary');
  for (const c of CAMPOS_DEL_MEDICO) o[c] = (m && m[c]) || '';
  if (o.direccion && !o.ciudad && !o.estado && !o.zip) {
    const p = consultaDeUnaLinea(o.direccion);
    if (p.city || p.zip) {
      o.direccion = p.street;
      o.ciudad = p.city;
      o.estado = p.state;
      o.zip = p.zip;
    }
  }
  return o;
}

/**
 * La linea de debajo del nombre en la tarjeta: especialidad · centro · NPI.
 * Sin los vacios y sin separadores sueltos.
 */
export function resumenDelMedico(m, lang = 'es') {
  if (!m) return '';
  const es = lang !== 'en';
  return [
    (m.especialidad || '').trim(),
    (m.centro || '').trim(),
    (m.npi || '').trim() ? `NPI ${String(m.npi).trim()}` : '',
    (m.red || '').trim() ? (es ? `Acepta ${m.red}` : `Accepts ${m.red}`) : '',
  ].filter(Boolean).join(' · ');
}

/**
 * Llena el formulario con un proveedor de "Mis proveedores" (E-405): los datos
 * del proveedor, no los de la ficha de donde salió. Persona, notas y lo que ya
 * se hubiera escrito en red/centro sin dato en el proveedor se quedan como estén.
 */
export const CAMPOS_DEL_PROVEEDOR = ['nombre', 'especialidad', 'npi', 'centro', 'direccion', 'ciudad', 'estado', 'zip', 'telefono', 'fax', 'red'];
export function llenarConProveedorDeLaAgencia(form, p, hoy = new Date()) {
  const fecha = `${String(hoy.getMonth() + 1).padStart(2, '0')}/${String(hoy.getDate()).padStart(2, '0')}/${hoy.getFullYear()}`;
  const o = { ...form };
  for (const c of CAMPOS_DEL_PROVEEDOR) if (p && p[c]) o[c] = p[c];
  o.origen = `Directorio de proveedores de la agencia, ${fecha}`;
  return o;
}
